import React, { useState, useEffect, useRef } from 'react';
import { Bell, BellOff, Clock, Calendar, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { requestPushPermission } from '../lib/pushPermission';

const API = '/api';

// Ask for push permission once the reminder exists, unless already granted.
function askPushForReminder() {
    requestPushPermission('reminder').then((result) => {
        if (result === 'blocked') {
            toast.warning('Notifications are blocked. Please enable them in your browser/app settings.', { duration: 5000 });
        }
    });
}

const PRESETS = [
    { key: '15min', label: '15 min' },
    { key: '30min', label: '30 min' },
    { key: '1hour', label: '1 hour' },
    { key: 'tomorrow', label: 'Tomorrow' },
    { key: 'next_week', label: 'Next week' },
    { key: '1month', label: '1 month' },
];

export function ReminderPicker({ bookmarkId, bookmarkTitle, existingReminder, onReminderChange }) {
    const [open, setOpen] = useState(false);
    const [showCustom, setShowCustom] = useState(false);
    const [customDate, setCustomDate] = useState('');
    const [customTime, setCustomTime] = useState('');
    const [setting, setSetting] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
                setShowCustom(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const setReminder = async (preset) => {
        setSetting(true);
        try {
            const res = await fetch(`${API}/reminders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ bookmark_id: bookmarkId, preset }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Reminder set`);
                onReminderChange?.(data);
                setOpen(false);
                askPushForReminder();
            } else {
                toast.error('Failed to set reminder');
            }
        } catch {
            toast.error('Failed to set reminder');
        } finally {
            setSetting(false);
        }
    };

    const setCustomReminder = async () => {
        if (!customDate || !customTime) {
            toast.error('Please select date and time');
            return;
        }
        setSetting(true);
        try {
            const dt = new Date(`${customDate}T${customTime}`);
            const res = await fetch(`${API}/reminders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    bookmark_id: bookmarkId,
                    custom_datetime: dt.toISOString(),
                }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Reminder set for ${dt.toLocaleString()}`);
                onReminderChange?.(data);
                setOpen(false);
                askPushForReminder();
                setShowCustom(false);
            } else {
                toast.error('Failed to set reminder');
            }
        } catch {
            toast.error('Failed to set reminder');
        } finally {
            setSetting(false);
        }
    };

    const cancelReminder = async () => {
        if (!existingReminder) return;
        try {
            await fetch(`${API}/reminders/${existingReminder.reminder_id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            toast.success('Reminder cancelled');
            onReminderChange?.(null);
            setOpen(false);
        } catch {
            toast.error('Failed to cancel reminder');
        }
    };

    const hasReminder = !!existingReminder;

    return (
        <div className="relative" ref={menuRef}>
            <Button
                variant="ghost"
                size="sm"
                className={`rounded-full w-9 h-9 p-0 ${hasReminder ? 'text-primary' : 'text-muted-foreground'}`}
                onClick={() => setOpen(!open)}
                data-testid="reminder-btn"
            >
                {hasReminder ? (
                    <Bell className="w-4 h-4 fill-current" />
                ) : (
                    <Bell className="w-4 h-4" />
                )}
            </Button>

            {open && (
                <div className="absolute right-0 top-full mt-1 z-50 w-56 bg-white rounded-xl shadow-xl border border-border/50 overflow-hidden"
                     style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}
                     data-testid="reminder-menu">

                    <div className="px-3 py-2.5 border-b border-border/30 flex items-center justify-between">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {hasReminder ? 'Reminder set' : 'Remind me'}
                        </span>
                        <button onClick={() => { setOpen(false); setShowCustom(false); }} className="text-muted-foreground hover:text-foreground">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {hasReminder && !showCustom && (
                        <div className="px-3 py-2 bg-primary/5 border-b border-border/30">
                            <div className="flex items-center gap-2 text-xs">
                                <Clock className="w-3 h-3 text-primary" />
                                <span className="text-foreground">
                                    {new Date(existingReminder.remind_at).toLocaleString(undefined, {
                                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                    })}
                                </span>
                            </div>
                            <button
                                onClick={cancelReminder}
                                className="mt-1.5 flex items-center gap-1 text-[11px] text-red-500 hover:text-red-600"
                                data-testid="cancel-reminder-btn"
                            >
                                <BellOff className="w-3 h-3" />
                                Cancel reminder
                            </button>
                        </div>
                    )}

                    {!showCustom ? (
                        <div className="py-1">
                            {PRESETS.map(p => (
                                <button
                                    key={p.key}
                                    onClick={() => setReminder(p.key)}
                                    disabled={setting}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-secondary/60 transition-colors disabled:opacity-50"
                                    data-testid={`preset-${p.key}`}
                                >
                                    <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                                    <span>{p.label}</span>
                                </button>
                            ))}
                            <button
                                onClick={() => setShowCustom(true)}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-secondary/60 transition-colors border-t border-border/30"
                                data-testid="preset-custom"
                            >
                                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                                <span>Custom...</span>
                            </button>
                        </div>
                    ) : (
                        <div className="p-3 space-y-2.5">
                            <input
                                type="date"
                                value={customDate}
                                onChange={e => setCustomDate(e.target.value)}
                                className="w-full text-sm border rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                                data-testid="custom-date"
                            />
                            <input
                                type="time"
                                value={customTime}
                                onChange={e => setCustomTime(e.target.value)}
                                className="w-full text-sm border rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                                data-testid="custom-time"
                            />
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 rounded-lg text-xs"
                                    onClick={() => setShowCustom(false)}
                                >
                                    Back
                                </Button>
                                <Button
                                    size="sm"
                                    className="flex-1 rounded-lg text-xs"
                                    style={{ background: '#10b981', color: 'white' }}
                                    onClick={setCustomReminder}
                                    disabled={setting || !customDate || !customTime}
                                    data-testid="set-custom-btn"
                                >
                                    <Check className="w-3 h-3 mr-1" />
                                    Set
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Small indicator for dashboard cards
export function ReminderBadge({ hasReminder }) {
    if (!hasReminder) return null;
    return (
        <div className="absolute top-2 left-2 z-10 w-5 h-5 rounded-full bg-primary/90 flex items-center justify-center shadow-sm"
             data-testid="reminder-badge">
            <Bell className="w-2.5 h-2.5 text-white fill-white" />
        </div>
    );
}
